import type { AbsichtElement, Answer, Question, RunDraft, RunView } from '../shared/types.js';
import { autosave } from './autosave.js';
import { h, t, tobj } from './dom.js';
import { pageLong } from './render-shared.js';
import { renderWait } from './wait.js';

/** Call 3 compares the pages with the answers and writes the findings: about 50 seconds (README, timings). */
const BEFUND_MS = 50000;

export interface FragenActions {
  submit(answers: Answer[], absicht: AbsichtElement[] | null): Promise<void>;
  draft(fragen: NonNullable<RunDraft['fragen']>): Promise<unknown>;
}

interface Draft {
  value: string | string[] | null;
  other: string;
  followup: string;
  items: { label: string; value: string; art: AbsichtElement['art'] | null }[] | null;
  skipped: boolean;
}

export function renderFragen(view: RunView, actions: FragenActions): HTMLElement {
  const root = h('section', { class: 'stage on' });
  const questions = view.questions ?? [];
  // The draft on the server: the answers so far and the question that was on screen.
  const resumed = view.draft?.fragen ?? null;
  const drafts = new Map<string, Draft>();
  for (const q of questions) {
    const given = resumed?.answers.find((answer) => answer.id === q.id);
    drafts.set(q.id, {
      value: q.type === 'multiple' ? (Array.isArray(given?.value) ? given.value : []) : typeof given?.value === 'string' ? given.value : null,
      other: given?.other ?? '',
      followup: given?.followup ?? '',
      items: q.items ? (given?.items ?? q.items).map((item) => ({ ...item })) : null,
      skipped: given?.skipped ?? false
    });
  }
  const hint = view.hinweis ? h('p', { class: 'later', style: 'margin:0 0 16px' }, view.hinweis) : null;
  if (hint) root.append(hint);
  const holder = h('div');
  root.append(holder);
  const errorBox = h('p', { class: 'error', hidden: true });
  root.append(errorBox);

  let index = Math.min(resumed?.index ?? 0, Math.max(questions.length - 1, 0));
  const collect = (): Answer[] =>
    questions.map((q) => {
      const draft = drafts.get(q.id)!;
      return { id: q.id, skipped: draft.skipped, value: draft.value, other: draft.other || null, followup: draft.followup || null, items: draft.items };
    });
  // Progress survives a reload and travels with the link.
  const save = autosave(root, () => ({ index, answers: collect() }), actions.draft);

  const finish = async () => {
    // The wait for the Befund is a step of its own: the questions leave the screen, the bar starts with the request.
    const wait = renderWait({ strings: 'erstellen', id: 'writing', barId: 'wbar', expectedMs: BEFUND_MS });
    holder.hidden = true;
    if (hint) hint.hidden = true;
    errorBox.hidden = true;
    root.append(wait);
    window.scrollTo({ top: 0 });
    save.now();
    const answers = collect();
    const r0 = drafts.get('R0');
    const absicht = view.absicht && r0?.items ? view.absicht.map((element) => ({ ...element, text: r0.items!.find((item) => item.art === element.art)?.value ?? element.text })) : view.absicht;
    try {
      await actions.submit(answers, absicht ?? null);
    } catch (error) {
      wait.remove();
      holder.hidden = false;
      if (hint) hint.hidden = false;
      errorBox.textContent = (error as { message?: string }).message || t('lesen.errors.pipeline');
      errorBox.hidden = false;
    }
  };

  const show = (i: number) => {
    index = i;
    holder.replaceChildren();
    if (!questions.length) {
      holder.append(h('p', { class: 'lead' }, t('fragen.none_needed')));
      void finish();
      return;
    }
    // The confirmation of the description is not a Rückfrage: the count names the questions after it.
    const derived = questions.filter((q) => q.type !== 'confirm');
    const pos = { nr: derived.indexOf(questions[i]) + 1, total: derived.length, last: i + 1 === questions.length };
    holder.append(renderQuestion(questions[i], pos, drafts.get(questions[i].id)!, {
      next: () => (i + 1 < questions.length ? show(i + 1) : void finish()),
      back: i > 0 ? () => show(i - 1) : null,
      skip: () => {
        drafts.get(questions[i].id)!.skipped = true;
        i + 1 < questions.length ? show(i + 1) : void finish();
      },
      changed: save
    }));
    save.now();
    window.scrollTo({ top: 0 });
  };
  show(index);
  return root;
}

interface Nav {
  next(): void;
  back: (() => void) | null;
  skip(): void;
  /** The draft of the question changed. */
  changed(): void;
}

function renderQuestion(q: Question, pos: { nr: number; total: number; last: boolean }, draft: Draft, nav: Nav): HTMLElement {
  const types = tobj<Record<string, string>>('fragen.types');
  const box = h('div', { class: 'qbox on' });
  box.append(h('p', { class: 'qhead' }, q.type === 'confirm' ? t('fragen.confirm_head') : t('fragen.head', { n: pos.nr, total: pos.total }), ' ', h('span', { class: 'qtype' }, types[q.type] ?? '')));
  draft.skipped = false;
  const isLast = pos.last;
  const nextLabel = isLast ? t('fragen.show_befund') : t('fragen.next');

  if (q.type === 'confirm') {
    box.append(h('h2', {}, `${t('fragen.confirm_title')} `, h('b', {}, t('fragen.confirm_title_bold')), ` ${t('fragen.confirm_title_end')}`));
    const items = draft.items ?? [];
    const viewList = h('ul', { class: 'understood' }, items.map((item) => h('li', {}, h('span', { class: 'k' }, item.label), h('span', { class: 'v' }, item.value))));
    const inputs = items.map((item) => h('input', { type: 'text', value: item.value, 'aria-label': item.label, maxlength: 300 }) as HTMLInputElement);
    const editList = h('ul', { class: 'understood', hidden: true }, items.map((item, k) => h('li', {}, h('span', { class: 'k' }, item.label), h('span', {}, inputs[k]))));
    const ok = h('button', { class: 'btn', type: 'button' }, t('fragen.confirm_ok'));
    const edit = h('button', { class: 'btn secondary', type: 'button' }, t('fragen.confirm_edit'));
    let editing = false;
    edit.addEventListener('click', () => {
      if (!editing) {
        editing = true;
        editList.hidden = false;
        viewList.hidden = true;
        edit.textContent = t('fragen.confirm_apply');
        ok.hidden = true;
        return;
      }
      inputs.forEach((input, k) => {
        items[k].value = input.value.trim() || items[k].value;
      });
      viewList.replaceChildren(...items.map((item) => h('li', {}, h('span', { class: 'k' }, item.label), h('span', { class: 'v' }, item.value))));
      nav.changed();
      editing = false;
      editList.hidden = true;
      viewList.hidden = false;
      edit.textContent = t('fragen.confirm_edit');
      ok.hidden = false;
    });
    ok.addEventListener('click', () => {
      draft.items = items;
      draft.value = 'confirmed';
      nav.next();
    });
    box.append(viewList, editList, h('div', { class: 'actions' }, ok, edit));
    return box;
  }

  box.append(h('h2', {}, q.question));
  if (q.hint) box.append(h('p', { class: 'muted' }, q.hint));
  const next = h('button', { class: 'btn', type: 'button', disabled: true }, nextLabel);
  const valid = () => {
    let ok = false;
    if (q.type === 'text') ok = typeof draft.value === 'string' && draft.value.trim().length >= (q.min ?? 1) && draft.value.trim().length <= (q.max ?? 10000);
    else if (q.type === 'multiple') ok = Array.isArray(draft.value) && draft.value.length > 0;
    else ok = typeof draft.value === 'string' && draft.value.length > 0 && (draft.value !== 'other' || draft.other.trim().length >= 3);
    if (ok && q.followup && q.type !== 'multiple') ok = draft.followup.trim().length >= q.followup.min && draft.followup.trim().length <= q.followup.max;
    next.disabled = !ok;
    // Every change of the draft passes here.
    nav.changed();
  };

  const followupField = q.followup ? renderFollowup(q, draft, valid) : null;

  if (q.type === 'text') {
    const input = h('textarea', { rows: 3, maxlength: q.max ?? 180, 'aria-label': q.question }) as HTMLTextAreaElement;
    if (typeof draft.value === 'string') input.value = draft.value;
    const counter = h('span', { class: 'small muted' }, t('fragen.text_counter', { n: input.value.length, max: q.max ?? 180 }));
    input.addEventListener('input', () => {
      draft.value = input.value;
      counter.textContent = t('fragen.text_counter', { n: input.value.length, max: q.max ?? 180 });
      valid();
    });
    box.append(h('label', { class: 'field' }, input), h('div', { class: 'desc-tools' }, counter));
  } else if (q.type === 'dropdown') {
    const select = h('select', { 'aria-label': q.question }, h('option', { value: '' }, t('fragen.dropdown_placeholder')), (q.options ?? []).map((option) => h('option', { value: option.value }, option.text))) as HTMLSelectElement;
    const other = q.allow_other ? h('input', { type: 'text', placeholder: q.other_placeholder ?? t('fragen.other_placeholder'), hidden: draft.value !== 'other', value: draft.other, 'aria-label': t('fragen.other_label') }) : null;
    if (q.allow_other) select.append(h('option', { value: 'other' }, t('fragen.other_label')));
    if (typeof draft.value === 'string') select.value = draft.value;
    select.addEventListener('change', () => {
      draft.value = select.value || null;
      if (other) other.hidden = select.value !== 'other';
      if (followupField) followupField.update();
      valid();
    });
    other?.addEventListener('input', () => {
      draft.other = (other as HTMLInputElement).value;
      valid();
    });
    box.append(h('label', { class: 'field' }, select));
    if (other) box.append(other);
  } else if (q.kind === 'signal' && q.type === 'single') {
    const chips = (q.options ?? []).map((option) => {
      const chip = h('button', { class: option.value === draft.value ? 'chip sel' : 'chip', type: 'button' }, option.text);
      chip.addEventListener('click', () => {
        chips.forEach((c) => c.classList.remove('sel'));
        chip.classList.add('sel');
        draft.value = option.value;
        if (followupField) followupField.update();
        valid();
      });
      return chip;
    });
    box.append(h('div', { class: 'chips' }, chips));
  } else {
    const multiple = q.type === 'multiple';
    const name = `q-${q.id}`;
    const rows: { el: HTMLElement; input: HTMLInputElement; value: string }[] = [];
    const sync = () => {
      for (const row of rows) row.el.classList.toggle('sel', row.input.checked);
      if (multiple) draft.value = rows.filter((row) => row.input.checked).map((row) => row.value);
      else draft.value = rows.find((row) => row.input.checked)?.value ?? null;
      if (followupField) followupField.update();
      valid();
    };
    const options = [...(q.options ?? [])];
    // What the draft already holds comes back checked.
    const chosen = (value: string) => (Array.isArray(draft.value) ? draft.value.includes(value) : draft.value === value);
    for (const option of options) {
      const input = h('input', { type: multiple ? 'checkbox' : 'radio', name, value: option.value, checked: chosen(option.value) }) as HTMLInputElement;
      const el = h('label', { class: chosen(option.value) ? 'opt sel' : 'opt' }, input, h('span', {}, option.text, option.found_on ? h('span', { class: 'from' }, t('fragen.found_on', { page: pageLong(option.found_on) })) : null));
      input.addEventListener('change', () => {
        if (multiple && input.checked) {
          if (q.exclusive_value && option.value === q.exclusive_value) rows.forEach((row) => row.input !== input && (row.input.checked = false));
          else rows.forEach((row) => row.value === q.exclusive_value && (row.input.checked = false));
        }
        sync();
      });
      rows.push({ el, input, value: option.value });
      box.append(el);
    }
    if (q.allow_other) {
      const input = h('input', { type: multiple ? 'checkbox' : 'radio', name, value: 'other', checked: chosen('other') }) as HTMLInputElement;
      const text = h('input', { type: 'text', placeholder: q.other_placeholder ?? t('fragen.other_placeholder'), value: draft.other, 'aria-label': t('fragen.other_label') }) as HTMLInputElement;
      const el = h('label', { class: chosen('other') ? 'opt sel' : 'opt' }, input, h('span', {}, t('fragen.other_label'), text));
      input.addEventListener('change', sync);
      text.addEventListener('input', () => {
        draft.other = text.value;
        if (text.value && !input.checked) {
          input.checked = true;
          sync();
        }
        valid();
      });
      text.addEventListener('focus', () => {
        if (!input.checked) {
          input.checked = true;
          sync();
        }
      });
      rows.push({ el, input, value: 'other' });
      box.append(el);
    }
  }
  if (followupField) {
    box.append(followupField.el);
    followupField.update();
  }

  next.addEventListener('click', nav.next);
  const actions = h('div', { class: 'actions' });
  if (nav.back) {
    const back = h('button', { class: 'btn secondary', type: 'button' }, t('fragen.back'));
    back.addEventListener('click', nav.back);
    actions.append(back);
  }
  actions.append(next);
  const skip = h('button', { class: 'skip', type: 'button' }, t('fragen.skip'));
  skip.addEventListener('click', nav.skip);
  actions.append(skip);
  box.append(actions);
  valid();
  return box;
}

function renderFollowup(q: Question, draft: Draft, valid: () => void): { el: HTMLElement; update(): void } {
  const label = h('span');
  const input = h('input', { type: 'text', maxlength: q.followup!.max, placeholder: t('fragen.followup_placeholder'), value: draft.followup, 'aria-label': q.followup!.question }) as HTMLInputElement;
  input.addEventListener('input', () => {
    draft.followup = input.value;
    valid();
  });
  const el = h('label', { class: 'field', hidden: true }, label, input);
  const update = () => {
    const chosen = q.options?.find((option) => option.value === draft.value);
    el.hidden = !chosen;
    if (chosen) label.textContent = q.followup!.question.replace('[Signal]', chosen.text).replace('{signal}', chosen.text);
  };
  return { el, update };
}
